import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    Pets as PetsIcon,
    Search as SearchIcon,
    People as PeopleIcon,
    WarningAmber as WarningAmberIcon,
    CheckCircle as CheckCircleIcon,
    Home as HomeIcon,
    HealthAndSafety as HealthAndSafetyIcon,
    ThumbUp as ThumbUpIcon,
    ThumbDown as ThumbDownIcon,
    EmojiNature as EmojiNatureIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェアとペット、増える人気と課題' },
    { id: 'search', label: 'ペット可物件の探し方と選び方' },
    { id: 'rules', label: '共同生活でのルール作りのポイント' },
    { id: 'allergy', label: 'アレルギー対策と健康への配慮' },
    { id: 'merit-demerit', label: 'ペット飼育のメリットとデメリット' },
    { id: 'summary', label: 'まとめ：快適なペット共生ルームシェアに向けて' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアとペット、増える人気と課題
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ペットと暮らす喜びを、ルームメイトと分かち合う。そんな新しいライフスタイルが、近年ますます注目を集めています。<strong>ペット可物件でのルームシェア</strong>は、独身者や若年層だけでなく、幅広い世代に支持されるようになりました。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    しかし、ペットを飼いながらの共同生活には、物件探しやルール作り、健康面での注意など、クリアすべき課題も多いもの。今回は、<Chip label="ペット可物件の探し方" color="primary" size="small" sx={{ mr: 1 }} />や<Chip label="共同生活のルール" color="primary" variant="outlined" size="small" sx={{ mr: 1 }} />、<Chip label="アレルギー対策" color="primary" variant="outlined" size="small" sx={{ mr: 1 }} />など、ルームシェアでのペット飼育にまつわるリアルな事情と注意点を、専門的な視点で徹底解説します。
                </Typography>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/pet-share-living_roomshare-pet-care.jpg"
                        alt="明るい共有リビングでペットとルームメイトがくつろぐ様子。木目調の床、白いインテリア、犬と猫が仲良く過ごし、笑顔の住人たちがリラックスしている雰囲気。"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 8 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textAlign: 'center' }}>
                        ペットと共に過ごすあたたかなルームシェア生活。空間づくりやルールが心地よさの鍵。
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ペット可物件の探し方 */}
            <Box id="search" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <SearchIcon sx={{ mr: 1, verticalAlign: 'sub' }} />
                    ペット可物件の探し方と選び方
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ペットと一緒に住めるルームシェア物件は、通常の物件より選択肢が限られます。<strong>「ペット可」と「ペット相談可」</strong>の違いを理解し、ポイントを押さえて探すことが大切です。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                <HomeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                ペット可物件を探すコツ
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="不動産サイトで「ペット可」条件を必ず指定" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="「ペット相談可」は必ず具体的な条件を確認" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="犬・猫・小動物など種類や頭数制限をチェック" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="近隣や管理組合のルールも要確認" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                <PetsIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                物件選びで重視すべきポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><EmojiNatureIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="ペットの安全を考えた間取り・床材" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><EmojiNatureIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="脱走・飛び出し防止の設備があるか" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><EmojiNatureIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="共有スペースと専有スペースのバランス" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><EmojiNatureIcon color="success" /></ListItemIcon>
                                    <ListItemText primary="動物病院やペット関連施設が近いか" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <WarningAmberIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                    物件によっては敷金・礼金が高めに設定される場合や、退去時のクリーニング条件が厳しいケースも。契約前に必ず確認しましょう。
                </Typography>
                <Box sx={{ my: 3 }}>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textAlign: 'center' }}>
                        ペット用設備の整ったエントランスは、快適な共生生活の第一歩。
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 共同生活でのルール */}
            <Box id="rules" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <PeopleIcon sx={{ mr: 1, verticalAlign: 'sub' }} />
                    共同生活でのルール作りのポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ペットと暮らすルームシェアでは、お互いが気持ちよく過ごすための<strong>ルール作り</strong>が欠かせません。トラブルを防ぎ、より良い関係を築くには、以下のような具体的な取り決めが重要です。
                </Typography>
                <Card sx={{ mb: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                            ルームシェアでよくあるルール例
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon><PetsIcon color="primary" /></ListItemIcon>
                                <ListItemText primary="共有スペースでのペットの行動範囲・入室可否" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><PeopleIcon color="primary" /></ListItemIcon>
                                <ListItemText primary="掃除や臭い対策の当番・分担" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><WarningAmberIcon color="warning" /></ListItemIcon>
                                <ListItemText primary="トラブル時（鳴き声、アレルギー、破損など）の連絡・対応方法" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><PetsIcon color="primary" /></ListItemIcon>
                                <ListItemText primary="エサやり・散歩・トイレなどの日常ケアの担当" />
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
                <Typography variant="body2" color="text.secondary">
                    <CheckCircleIcon color="success" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    ルールは「書面化」し、入居時に全員で確認・同意しておくと安心です。また、定期的に見直すことも大切です。
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* アレルギー対策 */}
            <Box id="allergy" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <HealthAndSafetyIcon sx={{ mr: 1, verticalAlign: 'sub' }} />
                    アレルギー対策と健康への配慮
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームメイト候補には、動物アレルギーや喘息などの<strong>健康リスク</strong>を抱える人も少なくありません。事前にしっかり配慮し、快適な共生を目指しましょう。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                アレルギー対策の基本
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="入居前にアレルギーの有無を確認" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="ペットの毛やフケが飛散しづらい間取り・空気清浄機の設置" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="ペット専用スペースの明確化とゾーニング" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                                    <ListItemText primary="共用部のこまめな掃除・消臭対策" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                専門家からのアドバイス
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><HealthAndSafetyIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="ペットアレルギーのある方は短期間のルームシェアなどで影響を確認" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><HealthAndSafetyIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="ペットの種類によってアレルゲン量が異なるため事前に情報共有" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><HealthAndSafetyIcon color="error" /></ListItemIcon>
                                    <ListItemText primary="定期的な換気、カーペットや布製品の清掃も重要" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/pet-allergy-measures_roomshare-pet-care.jpg"
                        alt="空気清浄機とペット専用ベッドが設置された清潔な共用リビング。床はフローリングで、明るい光が差し込み、健康を意識した生活環境。"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textAlign: 'center' }}>
                        アレルギー対策には設備と日常のケアの両立が不可欠。住人同士の配慮が快適な暮らしにつながる。
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* メリット・デメリット */}
            <Box id="merit-demerit" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ペット飼育のメリットとデメリット
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <ThumbUpIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        メリット
                                    </Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><PetsIcon color="primary" /></ListItemIcon>
                                        <ListItemText primary="癒しやストレス軽減効果" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><PeopleIcon color="primary" /></ListItemIcon>
                                        <ListItemText primary="ルームメイト同士の交流が深まる" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><EmojiNatureIcon color="success" /></ListItemIcon>
                                        <ListItemText primary="生活リズムが整いやすい" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
                                        <ListItemText primary="ペットの世話を分担できる" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card sx={{ p: 2, bgcolor: '#ffebee', border: '1px solid #ffcdd2' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <ThumbDownIcon color="error" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        デメリット
                                    </Typography>
                                </Box>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><WarningAmberIcon color="warning" /></ListItemIcon>
                                        <ListItemText primary="アレルギーや健康トラブルのリスク" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><WarningAmberIcon color="warning" /></ListItemIcon>
                                        <ListItemText primary="騒音や臭いによるトラブル" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><WarningAmberIcon color="warning" /></ListItemIcon>
                                        <ListItemText primary="掃除や世話の負担・分担トラブル" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><WarningAmberIcon color="warning" /></ListItemIcon>
                                        <ListItemText primary="退去時の原状回復費用が高い場合も" />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Typography variant="body2" sx={{ mt: 2 }}>
                    メリットとデメリットをしっかり把握し、ルームメイトが納得のうえでルールを決めることが、快適なペット共生ルームシェアのコツです。
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ：快適なペット共生ルームシェアに向けて
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでペットと暮らすためには、<strong>物件選び</strong>・<strong>ルール作り</strong>・<strong>健康配慮</strong>の3本柱が欠かせません。一人ひとりの思いやりと、小さな工夫が、豊かで心地よい共生生活を実現します。
                </Typography>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/pet-share-harmony_roomshare-pet-care.jpg"
                        alt="窓から光が差し込む明るいキッチンで、猫と犬が穏やかに過ごし、住人たちも笑顔で食事や会話を楽しむ共生のハーモニー。"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textAlign: 'center' }}>
                        ペットも人も心地よく暮らせるルームシェア。小さな配慮が大きな安心を生み出します。
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    「ルームシェア×ペット」の可能性を広げるために、あなたの暮らしにもぜひ参考にしてみてください。
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-pet-care',
    slug: 'roomshare-pet-care',
    title: 'ルームシェアでのペット飼育事情と注意点',
    excerpt: 'ペット可物件の探し方、共同生活でのルール、アレルギー対策、メリット・デメリットまで、ルームシェアでペットと快適に暮らすための実践的ガイド。',
    coverImage: '/images/blog/cover-roomshare-pet-care_roomshare-pet-care.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちがペットと共にリラックスし、笑顔で会話を楽しんでいる様子。インテリアは木目と白を基調にし、温かみと開放感が感じられる空間。',
    publishedAt: '2024-06-16T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', 'ペット', '暮らしのコツ'],
    tags: ['ペット可物件', 'ルール作り', 'アレルギー対策', '共生生活', 'メリット・デメリット'],
    author: {
        name: '西村 大輔',
        avatar: '/images/avatars/nishimura.jpg',
        bio: '住まいと暮らしの分野で長年情報発信。多様な生活スタイルや共生住宅の専門知識に基づいた実践的アドバイスを提供。',
        profilePicture: '/images/avatars/nishimura.jpg'
    },
    viewCount: 1320,
    contentComponent: PostContent
};

export default post;